'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { adminAuthService, AdminUser } from '@/lib/supabase/adminAuthService';

interface AdminAuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshSession: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // Check if user is already logged in and validate session
    const storedUser = localStorage.getItem('adminUser');
    const sessionTimestamp = localStorage.getItem('adminSessionTimestamp');
    
    if (storedUser && sessionTimestamp) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const timestamp = parseInt(sessionTimestamp, 10);
        const now = Date.now();
        
        // Check if session has expired
        if (now - timestamp < SESSION_TIMEOUT) {
          setUser(parsedUser);
        } else {
          // Session expired, clear storage
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminSessionTimestamp');
        }
      } catch (error) {
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminSessionTimestamp');
      }
    }
    setIsLoading(false);
  }, []);

  // Auto-refresh session timestamp every 5 minutes if user is active
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(() => {
      const sessionTimestamp = localStorage.getItem('adminSessionTimestamp');
      if (sessionTimestamp) {
        const timestamp = parseInt(sessionTimestamp, 10);
        const now = Date.now();
        
        // If session is still valid, refresh the timestamp
        if (now - timestamp < SESSION_TIMEOUT) {
          localStorage.setItem('adminSessionTimestamp', now.toString());
        } else {
          // Session expired, logout
          logout();
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [user]);

  const login = async (username: string, password: string) => {
    try {
      const result = await adminAuthService.login(username, password);
      
      if (result.success && result.user) {
        setUser(result.user);
        const now = Date.now();
        localStorage.setItem('adminUser', JSON.stringify(result.user));
        localStorage.setItem('adminSessionTimestamp', now.toString());
        return { success: true };
      }

      return { success: false, error: result.error || 'Login failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminSessionTimestamp');
    router.push('/admin/login');
  };

  const refreshSession = () => {
    if (user) {
      const now = Date.now();
      localStorage.setItem('adminSessionTimestamp', now.toString());
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshSession,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}